"use client";
import React, { CSSProperties, useMemo } from "react";

import { useTranslations } from "next-intl";

import { useCurrencyStore } from "@/store/currency.store";
import { ConvertedPrices } from "@/types/pricing.types";
import { cn } from "@/utils/cn";
import { formatConvertedPrice } from "@/utils/formatNumberWithSeparator";

import { AmountVariant } from "./types";

interface Props {
  // Pre-calculated prices from backend
  convertedPrices?: ConvertedPrices;
  variant?: AmountVariant;
  style?: CSSProperties;
  className?: string;
}

// eslint-disable-next-line react/display-name
const Component = React.memo(
  ({
    convertedPrices,
    style,
    className,
    variant = AmountVariant.FULL,
  }: Props) => {
    const { currency } = useCurrencyStore();
    const t = useTranslations();

    // Calculate amount using pre-calculated prices from backend
    const amount = useMemo(() => {
      if (
        convertedPrices &&
        convertedPrices[currency.value as keyof ConvertedPrices]
      ) {
        return formatConvertedPrice(
          convertedPrices[currency.value as keyof ConvertedPrices],
        );
      }

      // Debug logging for missing prices
      if (process.env.NODE_ENV === "development") {
        console.warn("Amount component: No converted price available", {
          currency: currency.value,
          convertedPrices,
          hasConvertedPrices: !!convertedPrices,
          availableCurrencies: convertedPrices
            ? Object.keys(convertedPrices)
            : [],
        });
      }

      // No price data available
      return "0";
    }, [convertedPrices, currency.value]);

    const currencySymbol = currency.symbol;

    return (
      <span
        className={cn("text-nowrap space-x-2", className)}
        style={style}
      >
        {(variant === AmountVariant.FULL ||
          variant === AmountVariant.TABLE) && <span>{currencySymbol}</span>}
        <span>{amount}</span>
        {variant === AmountVariant.PER_SQUARE && (
          <span>
            {currencySymbol}
            &nbsp;/&nbsp;
            {t("m2")}
          </span>
        )}
        {variant === AmountVariant.PER_MONTH && (
          <span>
            {currencySymbol}
            &nbsp;/&nbsp;
            {t("month")}
          </span>
        )}
        {variant === AmountVariant.SMALL && <span>{currencySymbol}</span>}
      </span>
    );
  },
);

Component.displayName = "Amount";

export const Amount = Component;
